import jwt_decode from 'jwt-decode'
import { ENDPOINT_URL, FLUSH_INTERVAL_MS, MAX_BACKOFF_MS, MAX_RETRY_BATCHES, REFRESH_COOLDOWN_MS, isProd, RETRY_QUEUE_PREFIX } from './config'

export function randomId() {
  return (
    (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
    `${Date.now()}-${Math.random().toString(16).slice(2)}`
  )
}

// One id per tab so tabs don't mix up their retry lists.
const TAB_ID = randomId()
const RETRY_QUEUE_KEY = RETRY_QUEUE_PREFIX + TAB_ID

// sessionStorage outlives a reload but TAB_ID doesn't, so take over what earlier page loads of this tab left queued.
function adoptLeftoverBatches() {
  try {
    const leftover = Object.keys(sessionStorage).filter((k) => k.startsWith(RETRY_QUEUE_PREFIX) && k !== RETRY_QUEUE_KEY)
    if (!leftover.length) return
    let batches = []
    for (const key of leftover) {
      try {
        batches = batches.concat(JSON.parse(sessionStorage.getItem(key) || '[]'))
      } catch (err) {
        // Unreadable, nothing to keep.
      }
      sessionStorage.removeItem(key)
    }
    if (batches.length) sessionStorage.setItem(RETRY_QUEUE_KEY, JSON.stringify(batches.slice(-MAX_RETRY_BATCHES)))
  } catch (err) {
    if (!isProd()) console.warn('[tracking] could not adopt earlier retry batches:', err)
  }
}
adoptLeftoverBatches()

// While the pipeline is overloaded or down, nothing is sent before this time.
let pausedUntil = 0
let backoffMs = 0

function isPaused() {
  return Date.now() < pausedUntil
}

function pause(retryAfterMs) {
  // Two sends failing in the same tick shouldn't double the wait twice.
  if (isPaused() && !retryAfterMs) return
  backoffMs = retryAfterMs || (backoffMs ? backoffMs * 2 : FLUSH_INTERVAL_MS)
  backoffMs = Math.min(backoffMs, MAX_BACKOFF_MS)
  pausedUntil = Date.now() + backoffMs
}

function resume() {
  backoffMs = 0
  pausedUntil = 0
}

// The server answered, so it's up, unless it said it's overloaded or down.
function recordResult(result) {
  if (result.ok || !result.retryable) resume()
  else pause(result.retryAfterMs)
}

// Retry-After is seconds or an HTTP date. Cross-origin, the browser hides it
// unless the server lists it in Access-Control-Expose-Headers.
function parseRetryAfter(response) {
  const value = response.headers.get('Retry-After')
  if (!value) return null
  const seconds = Number(value)
  const ms = Number.isNaN(seconds) ? Date.parse(value) - Date.now() : seconds * 1000
  return Number.isFinite(ms) && ms > 0 ? ms : null
}

async function logRejected(response) {
  try {
    const body = await response.json()
    if (Array.isArray(body.rejected) && body.rejected.length > 0) {
      console.warn('[tracking] pipeline rejected events, dropping them:', body.rejected)
    }
  } catch (err) {
    // No readable body, nothing to report.
  }
}

let refreshing = null
let refreshBlockedUntil = 0
let getToken = () => null
let setToken = () => {}
import('../utils/tokenCheck').then((module) => {
  getToken = module.getToken
  setToken = module.setToken
})

function refreshToken() {
  if (!refreshing) {
    refreshing = (async () => {
      // Loaded here, not at the top, because the store imports the tracker.
      const [{ default: store }, { refreshKeycloakToken }] = await Promise.all([
        import('../store'),
        import('../utils/apiFunctions'),
      ])
      // Guests have no refresh token.
      const refreshTokenValue = store.getters['userVUEX/getUserRefreshToken']
      if (!refreshTokenValue || refreshTokenValue === 'null') return false
      if (!jwt_decode(getToken()).iss?.includes('/auth/realms/')) return false
      const { data } = await refreshKeycloakToken(refreshTokenValue)
      setToken(data.access_token)
      store.commit('userVUEX/SET_TOKEN', data.access_token)
      return true
    })()
      .catch((err) => {
        if (!isProd()) console.warn('[tracking] token refresh failed:', err)
        return false
      })
      .finally(() => {
        refreshing = null
      })
  }
  return refreshing
}

async function postEvents(events, isRetryAfterRefresh = false) {
  try {
    const response = await fetch(ENDPOINT_URL, {
      method: 'POST',
      keepalive: true,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events }),
    })
    if (response.ok) {
      // Rejected events are malformed, so resending them would fail the same way.
      if (!isProd()) await logRejected(response)
      return { ok: true }
    }

    if (response.status === 401 && Date.now() >= refreshBlockedUntil) {
      if (!isRetryAfterRefresh && (await refreshToken())) return postEvents(events, true)
      refreshBlockedUntil = Date.now() + REFRESH_COOLDOWN_MS
    }

    if (!isProd()) console.warn('[tracking] batch rejected:', response.status)
    // Only 429 (slow down) and 503 (down) are temporary.
    if (response.status === 429 || response.status === 503) {
      return { ok: false, retryable: true, retryAfterMs: parseRetryAfter(response) }
    }
    return { ok: false, retryable: false }
  } catch (err) {
    // Usually the server or its ingress is down.
    if (!isProd()) console.warn('[tracking] batch send failed:', err)
    return { ok: false, retryable: true }
  }
}

function queueForRetry(events) {
  try {
    const existing = JSON.parse(sessionStorage.getItem(RETRY_QUEUE_KEY) || '[]')
    existing.push(events)
    sessionStorage.setItem(RETRY_QUEUE_KEY, JSON.stringify(existing.slice(-MAX_RETRY_BATCHES)))
  } catch (err) {
    if (!isProd()) console.warn('[tracking] could not queue batch for retry:', err)
  }
}

// Resolves to what happened to the batch: sent, queued (kept for retry), dropped or skipped (signed out).
export async function sendBatch(events) {
  if (!getToken()) return 'skipped'
  if (isPaused()) {
    queueForRetry(events)
    return 'queued'
  }
  const result = await postEvents(events)
  recordResult(result)
  if (result.ok) return 'sent'
  if (result.retryable) {
    queueForRetry(events)
    return 'queued'
  }
  return 'dropped'
}

let draining = null

export function drainRetryQueue() {
  if (!draining) {
    draining = (async () => {
      if (isPaused()) return
      let entries
      try {
        const stored = sessionStorage.getItem(RETRY_QUEUE_KEY)
        // Taken out now so batches queued while this sends go into a fresh list.
        sessionStorage.removeItem(RETRY_QUEUE_KEY)
        entries = JSON.parse(stored || '[]')
      } catch (err) {
        return
      }
      if (!entries.length) return

      const stillFailing = []
      for (const events of entries) {
        // Once paused, keep the rest for later instead of sending them into an outage.
        if (isPaused()) {
          stillFailing.push(events)
          continue
        }
        const result = await postEvents(events)
        recordResult(result)
        if (!result.ok && result.retryable) {
          stillFailing.push(events)
        }
      }

      try {
        const queuedMeanwhile = JSON.parse(sessionStorage.getItem(RETRY_QUEUE_KEY) || '[]')
        const remaining = stillFailing.concat(queuedMeanwhile).slice(-MAX_RETRY_BATCHES)
        if (remaining.length) sessionStorage.setItem(RETRY_QUEUE_KEY, JSON.stringify(remaining))
      } catch (err) {
        if (!isProd()) console.warn('[tracking] could not requeue failed batches:', err)
      }
    })().finally(() => {
      draining = null
    })
  }
  return draining
}

export function clearRetryQueue() {
  try {
    sessionStorage.removeItem(RETRY_QUEUE_KEY)
  } catch (err) {
    if (!isProd()) console.warn('[tracking] could not clear retry queue:', err)
  }
}

export function sendBatchBeacon(events) {
  if (!getToken()) return
  try {
    const blob = new Blob([JSON.stringify({ events })], {
      type: 'text/plain;charset=UTF-8',
    })
    const accepted = navigator.sendBeacon(ENDPOINT_URL, blob)
    if (!accepted && !isProd()) {
      console.warn('[tracking] sendBeacon rejected payload (probably too large)')
    }
  } catch (err) {
    if (!isProd()) {
      console.warn('[tracking] sendBeacon threw:', err)
    }
  }
}
